/**
 * AdminWeeklyDigestPage — preview + send-now UI for the Monday digest email.
 *
 * Calls:
 *   GET  /api/communications/weekly-digest/preview  → { context, html, recipients }
 *   POST /api/communications/weekly-digest/send     → { sent, skipped, context, recipients }
 *
 * Admins can review the computed numbers, see the rendered HTML, edit the
 * recipient list (comma-separated), and fire the email without waiting for
 * Monday 8am cron.
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Loader2, Send, RefreshCw, Mail, TrendingUp, TrendingDown, Minus,
  DollarSign, Users, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

function DeltaBadge({ pct }) {
  const Icon = pct > 0 ? TrendingUp : pct < 0 ? TrendingDown : Minus;
  const color = pct > 0 ? 'text-emerald-700' : pct < 0 ? 'text-rose-700' : 'text-muted-foreground';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`}>
      <Icon className="h-3 w-3" /> {Math.abs(pct)}%
    </span>
  );
}

function MetricCard({ icon: Icon, label, value, prev, deltaPct, color }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
            <p className="text-xs text-muted-foreground mt-1">
              vs {prev} last week · <DeltaBadge pct={deltaPct} />
            </p>
          </div>
          <Icon className={`h-6 w-6 ${color} opacity-60`} />
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminWeeklyDigestPage() {
  const { api } = useAuth();
  const [preview, setPreview] = useState(null);
  const [recipients, setRecipients] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const fetchPreview = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/communications/weekly-digest/preview/');
      setPreview(data);
      setRecipients((data.recipients || []).join(', '));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load digest preview');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const sendNow = async () => {
    if (!window.confirm(`Send the digest to ${recipients.split(',').filter(Boolean).length} recipient(s) right now?`)) return;
    setSending(true);
    try {
      const { data } = await api.post('/communications/weekly-digest/send/', { recipients });
      toast.success(`Sent to ${data.sent} recipient${data.sent === 1 ? '' : 's'}${data.skipped ? ` · ${data.skipped} skipped` : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const ctx = preview?.context;

  return (
    <div className="space-y-5" data-testid="admin-weekly-digest-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Mail className="h-6 w-6 text-primary" /> Weekly Revenue Digest
          </h1>
          <p className="text-sm text-muted-foreground">
            Auto-generated Monday 8am summary · preview below reflects the last 7 days
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchPreview} data-testid="refresh-digest-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button onClick={sendNow} disabled={sending || !recipients.trim()} data-testid="send-digest-btn">
            {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            Send now
          </Button>
        </div>
      </div>

      {/* Metrics */}
      {ctx && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <MetricCard
            icon={Users}
            label="New enrollments"
            value={ctx.enrollments_new}
            prev={ctx.enrollments_new_prev}
            deltaPct={ctx.enrollments_new_delta_pct}
            color="text-emerald-600"
          />
          <MetricCard
            icon={DollarSign}
            label="Revenue"
            value={`$${Number(ctx.revenue).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
            prev={`$${Number(ctx.revenue_prev).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
            deltaPct={ctx.revenue_delta_pct}
            color="text-blue-600"
          />
          <MetricCard
            icon={XCircle}
            label="Cancellations"
            value={ctx.cancellations}
            prev={ctx.cancellations_prev}
            deltaPct={ctx.cancellations_delta_pct}
            color="text-rose-600"
          />
        </div>
      )}

      {/* Top filling camps */}
      {ctx?.top_filling_camps?.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3">Top filling camps</h3>
            <div className="space-y-2">
              {ctx.top_filling_camps.map((c, idx) => (
                <div key={idx} className="flex items-center gap-3 py-2 border-b last:border-0 text-sm">
                  <Badge variant="outline" className="shrink-0">#{idx + 1}</Badge>
                  <span className="font-medium flex-1">{c.camp_name}</span>
                  <span className="text-muted-foreground text-xs">{c.location_name}</span>
                  <span className="font-semibold text-emerald-700 ml-auto">
                    {c.enrolled}/{c.capacity} ({Math.round(c.ratio * 100)}%)
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recipients */}
      <Card>
        <CardContent className="p-4">
          <Label className="text-xs uppercase tracking-wide">Recipients</Label>
          <Input
            value={recipients}
            onChange={e => setRecipients(e.target.value)}
            placeholder="admin1@example.com, admin2@example.com"
            className="mt-1"
            data-testid="digest-recipients-input"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Defaults to all active admin users. Comma-separated. Updates apply to the next "Send now" click.
          </p>
        </CardContent>
      </Card>

      {/* Email preview */}
      {preview?.html && (
        <Card>
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b bg-muted/30 flex items-center justify-between">
              <span className="text-sm font-medium">Email preview</span>
              <span className="text-[11px] text-muted-foreground">Renders as HTML in recipient inbox</span>
            </div>
            <iframe
              title="Weekly Digest Preview"
              srcDoc={preview.html}
              className="w-full"
              style={{ height: 640, border: 0 }}
              data-testid="digest-html-preview"
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
